import { Stack } from '@mui/material';
import { useQueries, useQuery } from '@tanstack/react-query';

import { api, getQueryProps, isDefined } from 'common';

import { useSchedulingConfigsField } from './hooks/useSchedulingConfigsField';
import { useFlowDefinitionContext } from '../../../../../context';
import { BlockAutocomplete } from '../../../ConfigFields/Autocomplete';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

interface SchedulingConfigsPracticeBlockProps {
  fieldNamePrefix: string;
}

const { FormBlockPracticeType } = api.FlowDefinition;

// FIXME: This is a temporary solution to avoid URL length limits (431 Request Header Fields Too Large).
// This file should not retrieve all records. The UI should implement a proper async paginated autocomplete, similar to other areas of code. Retrieve all doesn't scale
// Hack: Batch requests to avoid URL length limits (431 Request Header Fields Too Large)
// Split into chunks of 50 practice IDs per request
const BATCH_SIZE = 50;

export function SchedulingConfigsPracticeBlock(props: SchedulingConfigsPracticeBlockProps) {
  const { fieldNamePrefix } = props;
  const { value: schedulingConfigsValue } = useSchedulingConfigsField(fieldNamePrefix);
  const { value: practiceType } = useSelectedNodeField<api.FlowDefinition.FormBlockPracticeType>(
    `${fieldNamePrefix}.practiceType`
  );
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();

  const { data: practices } = useQuery({
    ...getQueryProps(api.client.Practice, 'findAll', { organizationId }),
  });

  const practiceIds = practices?.map((practice) => practice.id) ?? [];
  const practiceConfigBatches: string[][] = [];
  for (let i = 0; i < practiceIds.length; i += BATCH_SIZE) {
    practiceConfigBatches.push(practiceIds.slice(i, i + BATCH_SIZE));
  }

  const practiceConfigResults = useQueries({
    queries: practiceConfigBatches.map((batch) =>
      getQueryProps(api.client.Orchestrator.PracticeConfig, 'findAll', {
        practiceIds: batch,
      })
    ),
  });

  const practicesById = Object.fromEntries(
    practices?.map((practice) => [practice.id, practice]) ?? []
  );
  const practicesWithConfigById = Object.fromEntries(
    practiceConfigResults
      .map((query) => query.data)
      .flatMap((data) => data ?? [])
      .map((practiceConfig) => [
        practiceConfig.practiceId,
        practicesById[practiceConfig.practiceId],
      ])
  );

  if (!isDefined(schedulingConfigsValue)) {
    return null;
  }

  return (
    <Stack gap={1}>
      <BlockSelectField
        fieldName={`${fieldNamePrefix}.practiceType`}
        label="Practice Type"
        options={[
          { label: 'Static List', value: FormBlockPracticeType.StaticList },
          { label: 'Interpolated', value: FormBlockPracticeType.Interpolated },
        ]}
      />
      {practiceType === FormBlockPracticeType.StaticList && (
        <BlockAutocomplete
          fieldName={`${fieldNamePrefix}.practiceIds`}
          label="Practices"
          options={Object.keys(practicesWithConfigById || {})}
          getOptionLabel={(practiceId) =>
            practicesWithConfigById?.[practiceId]?.name
              ? `${practicesWithConfigById?.[practiceId]?.name} (${practiceId})`
              : practiceId
          }
          multiple
          helperText="Select a practice for the appointment"
        />
      )}
      {practiceType === FormBlockPracticeType.Interpolated && (
        <BlockValueTemplateField
          fieldName={`${fieldNamePrefix}.interpolatedPractice`}
          label="Interpolated Practice"
          helperText="Write the interpolated variable. e.g. {{blockName.data.field}}"
        />
      )}
    </Stack>
  );
}
